import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import type { DepositCryptoSelection } from "./DepositFundsCrypto";

interface DepositFundsConfirmationProps {
  selection: DepositCryptoSelection | null;
}

export default function DepositFundsConfirmation({
  selection,
}: Readonly<DepositFundsConfirmationProps>) {
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<number | null>(null);

  const walletAddress = selection?.walletAddress?.trim() ?? "";
  const tokenSymbol = selection?.tokenSymbol ?? "Token";
  const tokenDisplayName = selection?.tokenDisplayName ?? tokenSymbol;
  const networkDisplayLabel =
    selection?.networkDisplayLabel ?? selection?.networkLabel ?? "Network";

  const qrSource = useMemo(() => {
    if (selection?.walletQrCodeUrl) {
      return selection.walletQrCodeUrl;
    }

    if (!walletAddress) {
      return null;
    }

    const params = new URLSearchParams({
      size: "220x220",
      data: walletAddress,
      margin: "1",
    });

    return `https://api.qrserver.com/v1/create-qr-code/?${params.toString()}`;
  }, [selection?.walletQrCodeUrl, walletAddress]);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, []);

  const handleCopy = async () => {
    if (!walletAddress) {
      toast.error("No wallet address available to copy.");
      return;
    }

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }

      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success("Wallet address copied to clipboard.");

      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }

      resetTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        resetTimerRef.current = null;
      }, 2000);
    } catch {
      toast.error("Unable to copy wallet address. Please try again.");
    }
  };

  return (
    <div className="w-full mx-auto bg-transparent p-0 space-y-6">
      <header className="space-y-2">
        <p className="text-lg text-white font-medium">Deposit Funds</p>
        <p className="text-sm text-label-dark">
          Review the deposit details before you continue. Make sure the network
          matches your transfer and only send funds from wallets you control.
        </p>
      </header>

      <div className="border border-gray-700/60 rounded-lg p-4 space-y-4">
        <div className="flex items-start gap-3">
          <Avatar className="size-10 shrink-0">
            {selection?.tokenLogo ? (
              <AvatarImage src={selection.tokenLogo} alt={tokenSymbol} />
            ) : null}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
              {tokenSymbol.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wide text-label-dark">
              Selected Token
            </span>
            <span className="text-base text-white font-medium">
              {tokenSymbol}
            </span>
            <span className="text-xs text-muted-foreground">
              {tokenDisplayName}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col items-start">
            <span className="text-xs uppercase tracking-wide text-label-dark">
              Network
            </span>
            <Badge
              variant="secondary"
              className="bg-secondary/60 text-white pl-0">
              {networkDisplayLabel}
            </Badge>
          </div>
          {selection?.minDepositFormatted ? (
            <div className="flex flex-col items-start">
              <span className="text-xs uppercase tracking-wide text-label-dark">
                Minimum Deposit
              </span>
              <span className="text-sm text-white">
                {selection.minDepositFormatted}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        {qrSource ? (
          <div className="flex justify-center">
            <img
              src={qrSource}
              alt={`${tokenSymbol} wallet QR code`}
              className="h-48 w-48 rounded-xl bg-white p-4 object-contain"
            />
          </div>
        ) : (
          <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-700/60">
            <span className="text-xs text-label-dark">
              QR code not available for this wallet.
            </span>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-label-dark">
              Your {tokenSymbol} Address
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={!walletAddress}>
              {copied ? (
                <Check className="size-4 mr-2" />
              ) : (
                <Copy className="size-4 mr-2" />
              )}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <p className="font-mono text-xs text-white break-all rounded-lg bg-main-dark/40 p-3">
            {walletAddress || "Address not available"}
          </p>
        </div>
      </div>

      <div className="border-t border-gray-700/60 pt-4 text-xs text-label-dark">
        <p>
          Transfers sent to the wrong network or from a different asset cannot
          be recovered. Double-check the details above before confirming your
          deposit.
        </p>
      </div>
    </div>
  );
}
